import { initializeLogger } from '@seamless/logger';
import { getConnectionVehicleDataDispatchers, CTAData } from '@dh-io-globalelem/vehicle-data-store-connection';
import { SERVICE_NAMESPACE } from './consts';
import { LoadCTAConfigurationFunction } from './types';
import { DeeplinkKeys, ScenarioKeys } from './enums';
import {
  assignCtaScenario,
  loadDataFromDeeplinkService,
  mapResponse,
  makeDeepLinkServiceUrl,
  assignDynamicValues,
} from './utils';
import { provideDataFromAemEnvStoreOnce, provideDataFromVehicleDataStoreByCallback } from '../../utils';
import { CTAFormatted } from './types';
import { PROD_API_URL } from './consts';

const logger = initializeLogger(SERVICE_NAMESPACE);

//AESISU-877 - pointing API call always to PROD - incase of an improvement on lower environments remove this const and get the apiUrl from the AEM store
const apiUri = PROD_API_URL;

export const initializeCtaAutomationService = (): void =>
  provideDataFromVehicleDataStoreByCallback(async ({ modelSeries, subBrands, cta = {} }, unsubscribe) => {
    let ctasAssigned = false;
    const { runMode, country, language, vehicleData } = await provideDataFromAemEnvStoreOnce();
    const { informed, enquiry } = cta;

    let CtaScenarioKey = ScenarioKeys.Default;
    if (!apiUri || !modelSeries || ctasAssigned) {
      logger.error('No apiUri or modelSeries found, skipping CTA service initialization');
      return;
    }

    try {
      const deepLinkUrl = makeDeepLinkServiceUrl(apiUri, {
        runMode,
        country,
        language,
        modelSeries,
        subBrands,
      });

      let data = await loadDataFromDeeplinkService(deepLinkUrl);

      if (!data) {
        logger.error(`No data loaded from deep link service for ${modelSeries}.`);
        return;
      }

      if (Object.keys(enquiry as object).length === 0) {
        logger.error(`No enquiry configuration loaded for ${modelSeries}`, enquiry);
      }

      logger.log(`Data for ${modelSeries} has been loaded:`, data);

      data = {
        ...data,
        [DeeplinkKeys.Enquiry]: enquiry,
        [DeeplinkKeys.Informed]: informed,
      };

      if (vehicleData?.cta) {
        const tempCtaData = vehicleData.cta;
        CtaScenarioKey = tempCtaData.scenario as ScenarioKeys;
        logger.log(`Automated CTA scenario switched to ${CtaScenarioKey}`);
      }
      const scenario = assignCtaScenario(data, true, CtaScenarioKey);

      logger.log('Mapping buttons for the following scenario:', scenario);

      const mappedCtas = mapResponse(data, ...scenario);

      if (mappedCtas) {
        // Unsubscribe before updating store
        unsubscribe();
        ctasAssigned = true;
        const dispatchers = await getConnectionVehicleDataDispatchers();
        dispatchers.setCta(mappedCtas);

        logger.log(`Buttons for ${modelSeries} have been added to the store:`, mappedCtas);
      } else {
        logger.error(`No buttons for ${modelSeries} added to the store.`);
      }
    } catch (error: any) {
      logger.error(error.message);
    }
  });
export const initializeCtaStoreListener = (configuration?: string): void => {
  let ctasAssigned = false;
  provideDataFromVehicleDataStoreByCallback(async ({ cta, configurationId }, unsubscribe) => {
    if (configuration !== configurationId && cta && !ctasAssigned) {
      // Unsubscribe before updating store
      unsubscribe();

      const ctas = assignDynamicValues(cta, configurationId);
      ctasAssigned = true;

      const dispatchers = await getConnectionVehicleDataDispatchers();
      dispatchers.setCta(ctas as CTAData);
      logger.log(`Updated CTAs with configurationID: ${configurationId} added`);

      // Reinitialize the listener
      initializeCtaStoreListener(configurationId);
    }
  });
};

export const loadCtaConfiguration: LoadCTAConfigurationFunction = async (
  modelSeries,
  subBrands = [],
  enquiryCta?,
  informedCta?,
  CTAscenario?,
  configurationId?,
) => {
  const { country, language, runMode } = await provideDataFromAemEnvStoreOnce();

  const deepLinkUrl = makeDeepLinkServiceUrl(apiUri, { runMode, country, language, modelSeries, subBrands });

  const data = await loadDataFromDeeplinkService(deepLinkUrl);

  if (data) {
    logger.log(`Data for ${modelSeries} has been loaded:`, data);

    if (enquiryCta) {
      data[DeeplinkKeys.Enquiry] = enquiryCta;
    }

    if (informedCta) {
      data[DeeplinkKeys.Informed] = informedCta;
    }

    const scenario = assignCtaScenario(data, false, CTAscenario);

    logger.log('Mapping buttons for the following scenario:', scenario);

    const mappedCtas = mapResponse(data, ...scenario);

    if (mappedCtas) {
      const formattedCtas = assignDynamicValues(mappedCtas, configurationId ? configurationId : '');

      logger.log(`Buttons for ${modelSeries} have been mapped:`, formattedCtas);
      return formattedCtas as CTAFormatted;
    } else {
      logger.error(`No buttons for ${modelSeries} mapped.`);
    }
  }
};
